import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { Grid } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Tooltip from "@mui/material/Tooltip";
import CommonSelect from "components/CommonSelect/select";
import TitleCustom from "components/CustomTitle";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import Footer from "layouts/Footer";
import { useCallback, useEffect, useState } from "react";
import { deleteSalesByQuarterYearAndCompany, saleProductQuarterByCompanyAndQuarter } from "rest/requests";
import { companiesSync, openTemplateSalesReport, openTemplateWithProduct } from "rest/sync/requestsSync";
import DropzoneDialogExample from "./dropZone";
import EditSalesProduct from "./editSalesProduct";
import { getExcelData } from "./getExcelData";
import getTableData from "./getTableData";
import { quarters } from "./quartersCombo";

const toolTipText =
  "Se descargara un archivo excel con un formato definido. Usted puede agregar registros a este archivo y luego subirlo al sistema nuevamente";

function buildTitle(year, companies, selectedCompany, selectedQuarter, quartersEs) {
  try {
    var company = companies.filter((elem) => {
      return elem.id === selectedCompany;
    })[0].name;

    var _quarter = quartersEs.filter((elem) => {
      return elem.id === selectedQuarter || elem === selectedQuarter;
    })[0];
    
    const quarterName = typeof _quarter === 'object' ? _quarter.name : _quarter;
    
    return "Registros_Ventas_" + year + "_" + company + "_" + quarterName + ".xlsx";
  } catch {
    return "Registros_Ventas.xlsx";
  }
}

// Función para obtener el usuario de forma robusta
function getUserFromStorage() {
  // Buscar en todas las claves posibles
  const possibleKeys = [
    "user",
    "userData",
    "currentUser",
    "session",
    "auth",
    "token",
    "usuario"
  ];
  
  for (let key of possibleKeys) {
    try {
      const value = localStorage.getItem(key);
      if (value) {
        console.log(`✅ Usuario encontrado en clave: "${key}"`);
        
        // Intentar parsear
        let userData = JSON.parse(value);
        
        // Si sigue siendo string, parsear de nuevo
        if (typeof userData === 'string') {
          userData = JSON.parse(userData);
        }
        
        if (userData && userData.roles) {
          console.log("Roles encontrados:", userData.roles);
          return userData;
        }
      }
    } catch (e) {
      console.log(`Error con clave "${key}":`, e.message);
    }
  }
  
  // Buscar en sessionStorage
  for (let key of possibleKeys) {
    try {
      const value = sessionStorage.getItem(key);
      if (value) {
        console.log(`✅ Usuario encontrado en sessionStorage con clave: "${key}"`);
        let userData = JSON.parse(value);
        if (typeof userData === 'string') {
          userData = JSON.parse(userData);
        }
        if (userData && userData.roles) {
          return userData;
        }
      }
    } catch (e) {
      console.log(`Error con sessionStorage "${key}":`, e.message);
    }
  }
  
  // Buscar en cookies
  try {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (possibleKeys.includes(name) || name.includes("user") || name.includes("session")) {
        console.log(`✅ Usuario encontrado en cookie: "${name}"`);
        let userData = JSON.parse(decodeURIComponent(value));
        if (typeof userData === 'string') {
          userData = JSON.parse(userData);
        }
        if (userData && userData.roles) {
          return userData;
        }
      }
    }
  } catch (e) {
    console.log("Error con cookies:", e.message);
  }
  
  console.log("❌ No se encontró usuario en ningún almacenamiento");
  return null;
}

function RegisterSales() {
  const companiesCatalog = companiesSync();

  const [currentUser, setCurrentUser] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [showLoadFile, setShowLoadFile] = useState(false);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [elementToEdit, setElementToEdit] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    const user = getUserFromStorage();
    setCurrentUser(user);
    
    // Debug en consola
    console.log("=== USUARIO CARGADO ===");
    console.log("Usuario:", user);
    if (user) {
      console.log("Roles:", user.roles);
      console.log("Es ADMIN:", user.roles?.some(r => r.role === "ADMIN"));
    }
  }, []);

  // Verificar si es admin con logs
  const isAdmin = Boolean(currentUser?.roles?.some(role => role?.role === "ADMIN"));
  console.log("isAdmin:", isAdmin);

  const handleYear = (event) => {
    setYear(Number(event.target.value));
  };

  const handleCompanyChange = (selectedValue) => {
    setSelectedCompany(selectedValue);
  };

  const handleQuarterChange = (selectedValue) => {
    setSelectedQuarter(selectedValue);
  };

  const handleDeleteByFilters = async () => {
    await deleteSalesByQuarterYearAndCompany(selectedCompany, selectedQuarter, year);
    setConfirmDeleteOpen(false);
    setDeleting((prev) => !prev);
  };

  const getData = useCallback(async () => {
    if (String(year).length == 4 && selectedCompany && selectedQuarter) {
      setProducts(
        await saleProductQuarterByCompanyAndQuarter(selectedCompany, selectedQuarter, year)
      );
    }
  }, [year, selectedCompany, selectedQuarter, deleting, editing]);

  useEffect(() => {
    getData();
  }, [year, selectedCompany, selectedQuarter]);

  useEffect(() => {
    getData();
  }, [deleting, editing]);

  useEffect(() => {
    if (!showLoadFile) {
      getData();
    }
  }, [showLoadFile]);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  let currentQuarterId = null;
  if (currentMonth >= 0 && currentMonth <= 2) currentQuarterId = 1;
  else if (currentMonth >= 3 && currentMonth <= 5) currentQuarterId = 2;
  else if (currentMonth >= 6 && currentMonth <= 8) currentQuarterId = 3;
  else if (currentMonth >= 9 && currentMonth <= 11) currentQuarterId = 4;
  
  let selectedQuarterNumber = null;
  if (selectedQuarter && Array.isArray(quarters)) {
    const quarterIndex = quarters.findIndex(q => q === selectedQuarter || q.id === selectedQuarter);
    if (quarterIndex !== -1) {
      selectedQuarterNumber = quarterIndex + 1;
    }
  }
  
  const isYearValid = year === currentYear;
  const isQuarterValid = selectedQuarterNumber === currentQuarterId;
  
  // Lógica simplificada
  const shouldEnableButtons = isAdmin 
    ? Boolean(selectedCompany && selectedQuarter)
    : Boolean(isQuarterValid && isYearValid && selectedCompany && selectedQuarter);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <TitleCustom
        title={"Registros de Ventas"}
        excelData={getExcelData(products)}
        excelName={buildTitle(year, companiesCatalog, selectedCompany, selectedQuarter, quarters)}
      ></TitleCustom>
      <MDBox bgColor="white" variant="gradient" borderRadius="lg" shadow="lg" opacity={1} p={2}>
        <Grid container spacing={3}>
          <Grid item>
            <MDInput type="number" label="Year" value={year} onChange={handleYear} />
          </Grid>
          <Grid item>
            <CommonSelect
              data={companiesCatalog}
              onChange={handleCompanyChange}
            ></CommonSelect>
          </Grid>

          <Grid item>
            <CommonSelect
              label="Trimestres"
              data={quarters}
              onChange={handleQuarterChange}
            ></CommonSelect>
          </Grid>
        </Grid>
        
        {/* Mostrar mensaje SOLO si no es admin y la fecha no es válida */}
        {!shouldEnableButtons && !isAdmin && selectedCompany && selectedQuarter && (
          <MDBox mt={2} mb={1}>
            <span style={{ color: '#ff6b6b', fontSize: '0.875rem' }}>
              Solo puede modificar ventas del trimestre y año actual.
            </span>
          </MDBox>
        )}
        
        {/* Mostrar mensaje si falta selección */}
        {!shouldEnableButtons && !selectedCompany && !selectedQuarter && (
          <MDBox mt={2} mb={1}>
            <span style={{ color: '#ff6b6b', fontSize: '0.875rem' }}>
              Seleccione empresa y trimestre para continuar.
            </span>
          </MDBox>
        )}
        
        <MDButton
          variant="text"
          size="small"
          color="secondary"
          disabled={!shouldEnableButtons}
          onClick={() => {
            setElementToEdit(null);
            setEditing(true);
          }}
        >
          <AddIcon></AddIcon>
          Adicionar Ventas
        </MDButton>
        <Tooltip title={toolTipText}>
          <MDButton
            disabled={!shouldEnableButtons}
            variant="text"
            color="success"
            onClick={() => openTemplateSalesReport(selectedCompany)}
          >
            <DownloadIcon></DownloadIcon>
            Plantilla Registro Ventas
          </MDButton>
        </Tooltip>
        <Tooltip title={"Plantilla excel de ventas por trimestre"}>
          <MDButton
            disabled={!shouldEnableButtons}
            variant="text"
            color="info"
            onClick={() => openTemplateWithProduct(selectedCompany, selectedQuarter)}
          >
            <DownloadIcon></DownloadIcon>
            Plantilla Productos por Trimestre
          </MDButton>
        </Tooltip>
        <Tooltip title={"Eliminar todas las ventas del trimestre, año y empresa seleccionados"}>
          <MDButton
            disabled={!shouldEnableButtons}
            variant="text"
            color="error"
            onClick={() => setConfirmDeleteOpen(true)}
          >
            <DeleteIcon></DeleteIcon>
            Eliminar Ventas
          </MDButton>
        </Tooltip>
        <Tooltip title={"Subir plantilla con ventas previamente adicionadas"}>
          <MDButton
            disabled={!shouldEnableButtons}
            variant="text"
            color="dark"
            onClick={() => setShowLoadFile(true)}
          >
            <FileUploadIcon></FileUploadIcon>
            Cargar productos
          </MDButton>
        </Tooltip>
        <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Está seguro que desea eliminar todas las ventas para el año {year}, empresa y
              trimestre seleccionados? Esta acción no se puede deshacer.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => setConfirmDeleteOpen(false)} color="secondary">
              Cancelar
            </MDButton>
            <MDButton onClick={handleDeleteByFilters} color="error">
              Eliminar
            </MDButton>
          </DialogActions>
        </Dialog>
        {showLoadFile ? (
          <DropzoneDialogExample
            open={showLoadFile}
            setOpen={setShowLoadFile}
            year={year}
          ></DropzoneDialogExample>
        ) : (
          <div></div>
        )}
      </MDBox>
      <br></br>
      {editing ? (
        <EditSalesProduct
          saleToEdit={elementToEdit}
          setEditing={setEditing}
          selectedCompany={selectedCompany}
          selectedYear={year}
          selectedQuarterId={selectedQuarter}
        ></EditSalesProduct>
      ) : (
        <>
          <DataTable
            table={getTableData(products, setEditing, setElementToEdit, setDeleting, shouldEnableButtons)}
            canSearch={true}
          />
        </>
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default RegisterSales;